"use client";

/**
 * Single source of truth for the PWA install prompt.
 *
 * Browsers fire `beforeinstallprompt` exactly once per page load, and
 * you can only call `.prompt()` on that event inside a user gesture.
 * Multiple components used to each listen for the event separately,
 * which racy-captured it and produced stale `.prompt()` calls. This
 * hook collapses that into one listener installed at the module level
 * so every consumer sees the same deferred event.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/tracking";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

// Module-level state. One listener, many subscribers.
let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
// True until Chrome has had a chance to fire beforeinstallprompt (or
// we decide enough time has passed). Consumers render a skeleton
// while this is true to avoid a flicker where a "no install offer"
// state briefly shows before the prompt arrives.
let resolving = true;
const subscribers = new Set<() => void>();

// Upper bound on how long we'll wait for Chrome to fire
// beforeinstallprompt before deciding the event isn't coming. Picked
// to be longer than the realistic worst-case event delay (~800ms on
// slow devices) but short enough that Safari / Firefox users - which
// never fire the event - don't see a hung skeleton.
const RESOLVE_TIMEOUT_MS = 1500;

function notify() {
  subscribers.forEach((fn) => fn());
}

function subscribe(fn: () => void) {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

function getSnapshot() {
  // useSyncExternalStore wants a stable reference when nothing changed.
  // Returning an object literal every call would trigger re-renders on
  // every render cycle, so cache and rebuild only when state flips.
  return _snapshot;
}

let _snapshot = { canInstall: false, installed: false, resolving: true };

function refreshSnapshot() {
  _snapshot = {
    canInstall: deferred !== null && !installed,
    installed,
    resolving,
  };
  notify();
}

function finishResolving() {
  if (!resolving) return;
  resolving = false;
  refreshSnapshot();
}

let listenersWired = false;
function ensureListeners() {
  if (listenersWired || typeof window === "undefined") return;
  listenersWired = true;

  // Detect an already-installed PWA (iOS / Android). We know the
  // answer immediately in this case - no need to keep the skeleton up.
  if (window.matchMedia("(display-mode: standalone)").matches) {
    installed = true;
    resolving = false;
    refreshSnapshot();
  }

  // Chromium-only API that tells us whether this device has our PWA
  // installed - even while we're rendering inside a regular browser
  // tab, not the standalone window. Catches the "installed yesterday,
  // browsing today in Chrome" case that display-mode can't see.
  // Requires the manifest to declare itself under `related_applications`
  // with platform: "webapp" (done in /public/manifest.json).
  //
  // Silent failure path: Safari and Firefox don't implement the API,
  // so the try/catch is important even with the typeof guard. We
  // don't flip `installed` back to false on a null result - we don't
  // want to un-install a user we already detected via display-mode.
  type NavWithRelated = Navigator & {
    getInstalledRelatedApps?: () => Promise<Array<{ platform?: string; id?: string; url?: string }>>;
  };
  const nav = navigator as NavWithRelated;
  if (typeof nav.getInstalledRelatedApps === "function") {
    nav.getInstalledRelatedApps().then((apps) => {
      const hasWebapp = apps.some((a) => a.platform === "webapp");
      if (hasWebapp && !installed) {
        installed = true;
        resolving = false;
        refreshSnapshot();
      }
    }).catch(() => {
      // API present but rejected - private-mode, disabled, etc. Fall
      // back to display-mode + appinstalled heuristics.
    });
  }

  window.addEventListener("beforeinstallprompt", (e) => {
    // Suppress Chrome's auto mini-infobar; our own UI will own the
    // trigger moment so the copy + timing match our flows.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    // Chrome has decided the app is installable → stop resolving.
    resolving = false;
    refreshSnapshot();
  });

  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    resolving = false;
    refreshSnapshot();
    // Tracking: the user just installed the PWA. Fires for both
    // the in-page prompt path and the browser address-bar install
    // (Chrome's omnibox icon). Persisted to tracking_events so the
    // admin can see install volume + filter user lists by it.
    trackEvent("pwa.installed");
  });

  // Upper bound on how long we wait. After this, we commit to
  // "not installable for this browser" (Safari/Firefox path) so
  // consumers stop rendering skeletons indefinitely.
  setTimeout(finishResolving, RESOLVE_TIMEOUT_MS);
}

function getServerSnapshot() {
  // SSR has no install state; render the "not installable" variant.
  return _snapshot;
}

export function useInstallPrompt() {
  // Wire listeners lazily on first hook use so the module has no
  // side effects at import time.
  useEffect(() => {
    ensureListeners();
  }, []);

  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const promptInstall = useCallback(async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
    if (!deferred) return "unavailable";
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      // The event can only be used once; drop it either way.
      deferred = null;
      refreshSnapshot();
      // Tracking: distinguishes "user saw our install card and
      // accepted Chrome's prompt" from "user dismissed it". The
      // appinstalled-fired ``pwa.installed`` event is the source
      // of truth for actual completion - this captures the
      // intermediate decision point so we can separate a low
      // install rate due to "users not seeing the card" from
      // "users seeing the card and clicking Cancel".
      trackEvent("pwa.prompt.outcome", { outcome: choice.outcome });
      return choice.outcome;
    } catch {
      deferred = null;
      refreshSnapshot();
      trackEvent("pwa.prompt.outcome", { outcome: "error" });
      return "unavailable";
    }
  }, []);

  return {
    canInstall: state.canInstall,
    installed: state.installed,
    // True until Chrome has either fired beforeinstallprompt or the
    // grace window has expired. Render skeletons/spinners while this
    // is true so the button doesn't flicker between its two resolved
    // states on first paint.
    resolving: state.resolving,
    promptInstall,
  };
}

/**
 * Viewport-width gate for surfaces that should only nudge mobile users.
 * We match the Tailwind `md` breakpoint (768px) to stay consistent
 * with the rest of the UI's mobile/desktop split.
 */
export function useIsMobileViewport(): boolean {
  const state = useSyncExternalStore(
    (cb) => {
      if (typeof window === "undefined") return () => {};
      const mq = window.matchMedia("(max-width: 767px)");
      mq.addEventListener("change", cb);
      return () => mq.removeEventListener("change", cb);
    },
    () => (typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false),
    () => false,
  );
  return state;
}

/**
 * Detects whether the current page is running as an installed PWA
 * (standalone launcher) vs a regular browser tab. Used for:
 *   - Hiding install surfaces once the user has installed.
 *   - Switching homepage UI to teach the share-target flow.
 *   - Tagging API requests with an X-Display-Mode header for backend
 *     analytics on PWA adoption.
 *
 * Honours the three signals browsers expose today:
 *   - display-mode: standalone  (Android Chrome, desktop Chrome, Edge)
 *   - display-mode: minimal-ui  (some Firefox PWA variants)
 *   - navigator.standalone      (iOS Safari legacy flag)
 */
export function useIsPWA(): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === "undefined") return () => {};
      const mqStandalone = window.matchMedia("(display-mode: standalone)");
      const mqMinimal = window.matchMedia("(display-mode: minimal-ui)");
      mqStandalone.addEventListener("change", cb);
      mqMinimal.addEventListener("change", cb);
      return () => {
        mqStandalone.removeEventListener("change", cb);
        mqMinimal.removeEventListener("change", cb);
      };
    },
    () => {
      if (typeof window === "undefined") return false;
      return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.matchMedia("(display-mode: minimal-ui)").matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true
      );
    },
    () => false, // SSR default
  );
}

/**
 * Synchronous snapshot for non-React contexts (e.g. the api.ts fetch
 * wrapper). Safe to call anywhere - returns false during SSR.
 */

/**
 * Lightweight "has the user seen this surface before?" flag. Each
 * surface passes its own key so dismissals don't leak across them.
 *
 * Initial state is `true` (treated as "seen") until we've read
 * localStorage in an effect - this prevents hostile UI flashing in
 * during SSR/hydration. Once markSeen fires we flip in-memory so the
 * component unmounts immediately without waiting for another render.
 */
export function useDismissalFlag(key: string): [seen: boolean, markSeen: () => void] {
  // Default to "seen" so SSR renders nothing; the effect flips to the
  // real value right after hydration.
  const [seen, setSeen] = useState<boolean>(true);

  useEffect(() => {
    try {
      setSeen(localStorage.getItem(key) === "1");
    } catch {
      setSeen(false);
    }
  }, [key]);

  const markSeen = useCallback(() => {
    try {
      localStorage.setItem(key, "1");
    } catch {
      // private mode / disabled storage - nothing we can do
    }
    setSeen(true);
    // Overlays waiting their turn (the feedback prompt) re-check on this.
    window.dispatchEvent(new Event("menzil:overlay-gates"));
  }, [key]);

  return [seen, markSeen];
}
